import React from 'react';
import PropTypes from 'prop-types';
import Button from '../Button';
import Icon from '../Icon';
import Link from '../Link';
import Modal from '../Modal';
import ReservationSummaryImage from '../ReservationSummary/ReservationSummaryImage';
import CalendarIcon from '../../svg/icons/ic_calendar-solid_20x20.svg';
import PeopleIcon from '../../svg/icons/ic_guests_20x20.svg';
import Checkmark from '../../svg/icons/checkmark.svg';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import Close from '../../../../consumer_web/app/modules/svg/dynamic/close.svg';

import './RemoveNotifyList.scss';

const getTimeRange = (notify) => `${moment(`${notify.day} ${notify.time_preferred_start}`).format('llll')} - ${moment(notify.time_preferred_end, 'HH:mm').format('LT')}`;

const renderCloseButton = (onClose) => (
  <div className="RemoveNotifyList__header">
    <Button
      onClick={onClose}
      variant="no-style"
      className="Button__close"
      aria-label={getTranslation({
        translationKey: 'close',
        fallback: 'Close',
      })}
      data-test-id="remove-notify-button-close"
    >
      <Close />
    </Button>
  </div>
);

const RemoveNotifyList = ({
  isError,
  isSuccessful,
  notifies,
  onCloseModal,
  removeNotifies,
  selectedDate,
}) => {
  const formattedDate = moment(selectedDate, 'YYYY-MM-DD').format('LL');

  const primaryButtonText = isSuccessful
    ? (
      <span className="RemoveNotifyList__button--success">
        <Icon component={Checkmark} name="checkmark" />
        {getTranslation({ translationKey: 'removed', fallback: 'Removed' })}
      </span>
    )
    : getTranslation({ translationKey: 'remove_all', fallback: 'Remove All' });

  const renderNotifyCard = (notify) => {
    const { venue } = notify.results.venues[0];
    const { specs } = notify;
    const image = venue.responsive_images.originals[venue.responsive_images.file_names[0]]?.url;

    return (
      <div className="RemoveNotifyList__notify" key={`${specs.venue_id}-${specs.time_preferred_start}`}>
        <ReservationSummaryImage
          backgroundImage={image}
        />
        <div className="RemoveNotifyList__summary">
          <Link
            className="RemoveNotifyList__venue-name"
            href={`/cities/${venue.location.url_slug}/venues/${venue.url_slug}`}
            target="_blank"
          >
            {venue.name}
          </Link>
          <div className="RemoveNotifyList__details-row">
            <Icon component={CalendarIcon} name="calendar" />
            {getTimeRange(specs)}
          </div>
          <div className="RemoveNotifyList__details-row">
            <Icon component={PeopleIcon} name="people" />
            <TranslationProvider translationKey="number_guests" translationValues={{ number: `${specs.party_size}` }}>
              <span>{`${specs.party_size} Guests`}</span>
            </TranslationProvider>
          </div>
        </div>
      </div>
    );
  };

  if (isError) {
    return (
      <div className="RemoveNotifyList">
        <Modal
          isOpen
          shouldCloseOnEsc
          shouldCloseOnOverlayClick
          hasFooter
          primaryCTAText={getTranslation({ translationKey: 'close', fallback: 'Close' })}
          primaryCTAVariant="primary"
          primaryCTAAdditionalProps={{
            onClick: onCloseModal,
            'data-test-id': 'remove-notify-primary-cta',
          }}
          styles={{
            content: {
              maxWidth: '450px',
            },
          }}
        >
          {renderCloseButton(onCloseModal)}

          <TranslationProvider translationKey="remove_notify_error">
            <div className="RemoveNotifyList__error">
              Could Not Remove Notify
            </div>
          </TranslationProvider>
        </Modal>
      </div>
    );
  }

  return (
    <div className="RemoveNotifyList">
      <Modal
        isOpen
        shouldCloseOnEsc
        shouldCloseOnOverlayClick
        hasFooter
        hasSecondaryCTA
        primaryCTAText={primaryButtonText}
        primaryCTAVariant="primary"
        secondaryCTAText={getTranslation({ translationKey: 'no_thanks', fallback: 'No Thanks' })}
        secondaryCTAVariant="secondary"
        primaryCTAAdditionalProps={{
          onClick: removeNotifies,
          disabled: isSuccessful,
          'data-test-id': 'remove-notify-primary-cta',
        }}
        secondaryCTAAdditionalProps={{
          onClick: onCloseModal,
          'data-test-id': 'remove-notify-secondary-cta',
        }}
        styles={{
          content: {
            maxWidth: '450px',
          },
        }}
      >
        {renderCloseButton(onCloseModal)}

        <TranslationProvider translationKey="remove_notify_for_date" translationValues={{ date: formattedDate }}>
          <div className="RemoveNotifyList__title">
            {`Remove other Notify alerts for<br />${formattedDate}?`}
          </div>
        </TranslationProvider>

        <TranslationProvider translationKey="remove_notify_subtitle">
          <div className="RemoveNotifyList__subtitle">
            Looks like you have other alerts set on the same day. Do you want to remove those and give others a chance?
          </div>
        </TranslationProvider>

        <hr className="RemoveNotifyList__hr" />

        <div className="RemoveNotifyList__card-container">
          {notifies.map(renderNotifyCard)}
        </div>
      </Modal>
    </div>
  );
};

RemoveNotifyList.propTypes = {
  isError: PropTypes.bool,
  isSuccessful: PropTypes.bool,
  notifies: PropTypes.arrayOf(PropTypes.shape({
    image: PropTypes.string.isRequired,
    locationSlug: PropTypes.string.isRequired,
    venueId: PropTypes.number.isRequired,
    venueCityCode: PropTypes.string.isRequired,
    venueCityName: PropTypes.string.isRequired,
    venueName: PropTypes.string.isRequired,
    venueUrl: PropTypes.string.isRequired,
  })).isRequired,
  onCloseModal: PropTypes.func.isRequired,
  removeNotifies: PropTypes.func.isRequired,
  selectedDate: PropTypes.string.isRequired,
};

RemoveNotifyList.defaultProps = {
  isError: false,
  isSuccessful: false,
};

RemoveNotifyList.displayName = 'RemoveNotifyList';
export default RemoveNotifyList;
